// context/AuthContext.js - Контекст для управления аутентификацией (ИСПРАВЛЕНО)
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import apiService from '../services/api';
import { authStorage } from '../utils/storage';

// =============================================================================
// ТИПЫ ДЕЙСТВИЙ
// =============================================================================
const AUTH_ACTIONS = {
  // Состояния загрузки
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  
  // Аутентификация
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  REGISTER_START: 'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  LOGOUT: 'LOGOUT',
  
  // Пользователь
  SET_USER: 'SET_USER',
  UPDATE_USER: 'UPDATE_USER',
  
  // Инициализация
  INITIALIZE_AUTH: 'INITIALIZE_AUTH'
};

// =============================================================================
// НАЧАЛЬНОЕ СОСТОЯНИЕ
// =============================================================================
const initialState = {
  // Данные пользователя
  user: null,
  isAuthenticated: false,
  
  // Состояния загрузки
  isLoading: false,
  isInitializing: true,
  
  // Ошибки
  error: null,
  
  // Токен
  token: null
};

// =============================================================================
// REDUCER
// =============================================================================
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return { 
        ...state, 
        isLoading: action.payload, 
        error: action.payload ? null : state.error 
      };
      
    case AUTH_ACTIONS.SET_ERROR:
      return { 
        ...state, 
        error: action.payload, 
        isLoading: false 
      };
      
    case AUTH_ACTIONS.CLEAR_ERROR:
      return { 
        ...state, 
        error: null 
      };
      
    // Вход
    case AUTH_ACTIONS.LOGIN_START:
      return { 
        ...state, 
        isLoading: true, 
        error: null 
      };
      
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        error: null
      };
      
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        error: action.payload
      };
      
    // Регистрация  
    case AUTH_ACTIONS.REGISTER_START:
      return { 
        ...state, 
        isLoading: true, 
        error: null 
      };
      
    case AUTH_ACTIONS.REGISTER_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        error: null
      };
      
    case AUTH_ACTIONS.REGISTER_FAILURE:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        error: action.payload
      };
      
    // Выход
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        error: null,
        isLoading: false
      };
      
    // Обновление пользователя
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload
      };
      
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null
      };
      
    // Инициализация - КЛЮЧЕВОЙ МОМЕНТ
    case AUTH_ACTIONS.INITIALIZE_AUTH:
      return {
        ...state,
        isInitializing: false,
        isAuthenticated: action.payload.isAuthenticated,
        user: action.payload.user,
        token: action.payload.token
      };
      
    default:
      return state;
  }
};

// =============================================================================
// КОНТЕКСТ
// =============================================================================
const AuthContext = createContext(null);

// =============================================================================
// ПРОВАЙДЕР
// =============================================================================
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // =============================================================================
  // ИСПРАВЛЕННАЯ ИНИЦИАЛИЗАЦИЯ ПРИ ЗАГРУЗКЕ
  // =============================================================================
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = authStorage.getToken();
        const user = authStorage.getUser();
        const isTokenValid = authStorage.isTokenValid();
        const isUserValid = user && user.email;
        
        if (isTokenValid && isUserValid) {
          try {
            const response = await apiService.getProfile();
            
            if (response && response.id && response.email) {
              const updatedUser = {
                id: response.id,
                email: response.email,
                createdAt: response.createdAt || user.createdAt,
                lastLoginAt: response.lastLoginAt,
                ...user
              };
              
              dispatch({
                type: AUTH_ACTIONS.INITIALIZE_AUTH,
                payload: {
                  isAuthenticated: true,
                  user: updatedUser,
                  token
                }
              });
              return;
            }
          } catch (error) {
            if (error.type === 'AUTH_ERROR' || error.message?.includes('401')) {
              authStorage.clearAuthData();
            } else {
              dispatch({
                type: AUTH_ACTIONS.INITIALIZE_AUTH,
                payload: {
                  isAuthenticated: true,
                  user,
                  token
                }
              });
              return;
            }
          }
        }
        
        if (!isUserValid || !isTokenValid) {
          authStorage.clearAuthData();
        }
        
        dispatch({
          type: AUTH_ACTIONS.INITIALIZE_AUTH,
          payload: {
            isAuthenticated: false,
            user: null,
            token: null
          }
        });
        
      } catch (error) {
        console.error('Ошибка инициализации аутентификации:', error);
        dispatch({
          type: AUTH_ACTIONS.INITIALIZE_AUTH,
          payload: {
            isAuthenticated: false,
            user: null,
            token: null
          }
        });
      }
    };

    initializeAuth();
  }, []);

  // =============================================================================
  // ДЕЙСТВИЯ
  // =============================================================================
  
  /**
   * Вход пользователя
   */
  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    
    try {
      const response = await apiService.login(credentials);
      console.log('Login response:', response);
      
      if (response.success) {
        const { user, accessToken, expiresIn } = response;
        console.log('Login success data:', { user, accessToken, expiresIn });
        
        if (!accessToken) {
          throw new Error('Токен доступа не получен от сервера');
        }

        try {
          authStorage.setAuthData(accessToken, user, expiresIn);
        } catch (storageError) {
          console.error('Ошибка сохранения в localStorage:', storageError);
          throw new Error('Ошибка сохранения данных аутентификации');
        }

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token: accessToken }
        });
        
        return { success: true, user };
      } else {
        throw new Error(response.errorMessage || 'Ошибка входа');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.message || 'Неизвестная ошибка при входе';
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage
      });
      
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Регистрация пользователя
   */
  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });
    
    try {
      const response = await apiService.register(userData);
      console.log('Register response:', response);
      
      if (response.success) {
        const { user, accessToken, expiresIn } = response;
        console.log('Register success data:', { user, accessToken, expiresIn });
        
        if (!accessToken) {
          throw new Error('Токен доступа не получен от сервера');
        }

        try {
          authStorage.setAuthData(accessToken, user, expiresIn);
        } catch (storageError) {
          console.error('Ошибка сохранения в localStorage:', storageError);
          throw new Error('Ошибка сохранения данных аутентификации');
        }

        dispatch({
          type: AUTH_ACTIONS.REGISTER_SUCCESS,
          payload: { user, token: accessToken }
        });
        
        return { success: true, user };
      } else {
        throw new Error(response.errorMessage || 'Ошибка регистрации');
      }
    } catch (error) {
      console.error('Register error:', error);
      const errorMessage = error.message || 'Неизвестная ошибка при регистрации';
      
      dispatch({
        type: AUTH_ACTIONS.REGISTER_FAILURE,
        payload: errorMessage
      });
      
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Выход пользователя
   */
  const logout = async () => {
    try {
      if (state.token) {
        await apiService.logout();
      }
    } catch (error) {
      console.warn('Ошибка при выходе на сервере:', error);
    } finally {
      authStorage.clearAuthData();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  /**
   * Обновление данных пользователя
   */
  const updateUser = async (userData) => {
    try {
      const response = await apiService.updateProfile(userData);
      if (response.status === 'success') {
        const updatedUser = authStorage.updateUser(userData);
        if (updatedUser) {
          dispatch({
            type: AUTH_ACTIONS.UPDATE_USER,
            payload: userData
          });
        }
        return { success: true, user: response.data };
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления профиля');
      }
    } catch (error) {
      console.error('Ошибка обновления профиля:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Смена пароля
   */
  const changePassword = async (passwordData) => {
    try {
      const response = await apiService.changePassword(passwordData);
      if (response.status === 'success') {
        return { success: true, message: 'Пароль успешно изменен' };
      } else {
        throw new Error(response.error?.message || 'Ошибка смены пароля');
      }
    } catch (error) {
      console.error('Ошибка смены пароля:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Восстановление пароля
   */
  const forgotPassword = async (email) => {
    try {
      const response = await apiService.forgotPassword(email);
      if (response.status === 'success') {
        return { 
          success: true, 
          message: response.message || 'Ссылка для восстановления отправлена на email' 
        };
      } else {
        throw new Error(response.error?.message || 'Ошибка восстановления пароля');
      }
    } catch (error) {
      console.error('Ошибка восстановления пароля:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Очистка ошибок
   */
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  /**
   * Проверка валидности токена
   */
  const checkTokenValidity = () => {
    if (!state.token || !authStorage.isTokenValid()) {
      logout();
      return false;
    }
    return true;
  };

  /**
   * Обновление токена (для будущего использования)
   */
  const refreshToken = async () => {
    try {
      // TODO: Implement token refresh logic
      return true;
    } catch (error) {
      console.error('Ошибка обновления токена:', error);
      logout();
      return false;
    }
  };

  // =============================================================================
  // ЗНАЧЕНИЕ КОНТЕКСТА
  // =============================================================================
  const contextValue = {
    // Состояние
    ...state,
    
    // Действия
    login,
    register,
    logout,
    updateUser,
    changePassword,
    forgotPassword,
    clearError,
    checkTokenValidity,
    refreshToken,
    
    // Вспомогательные функции
    isTokenValid: () => authStorage.isTokenValid(),
    
    // Статусы для компонентов
    canAccess: (requiredAuth = true) => {
      if (requiredAuth) {
        return state.isAuthenticated && !state.isInitializing;
      }
      return !state.isInitializing;
    }
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// =============================================================================
// ХУКИ
// =============================================================================

/**
 * Хук для использования контекста аутентификации
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};

/**
 * Хук для проверки аутентификации с автоматическим редиректом
 */
export const useRequireAuth = () => {
  const auth = useAuth();
  
  useEffect(() => {
    if (!auth.isInitializing && !auth.isAuthenticated) {
      console.warn('Пользователь не аутентифицирован');
    }
  }, [auth.isInitializing, auth.isAuthenticated]);

  return auth;
};

/**
 * Хук для автоматической проверки токена
 */
export const useTokenValidator = (intervalMs = 60000) => {
  const { checkTokenValidity } = useAuth();
  
  useEffect(() => {
    const interval = setInterval(() => {
      checkTokenValidity();
    }, intervalMs);

    return () => clearInterval(interval);
  }, [checkTokenValidity, intervalMs]);
};

// =============================================================================
// ЭКСПОРТ
// =============================================================================
export default AuthContext;
export { AUTH_ACTIONS };